import {
  OnInit,
  Component,
  OnDestroy,
  Output,
  Input,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import moment from 'moment';
import {
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  interval,
  pairwise,
  startWith,
  takeUntil,
  throttle,
} from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import Message from 'app/modules/social/message/message';
import MessageType from 'app/modules/social/message/message-type';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from 'app/modules/social/modal-simple-social/modal-simple-social.component';
import {
  PhotoBoothViewerComponent,
  PhotoViewerData,
} from 'app/modules/social/photo-booth-viewer/photo-booth-viewer.component';
import { FramedPicture, PhotoBoothPicture, PhotoBoothService } from 'app/modules/social/services/photo-booth.service';
import { MediaEmbedType, PresentationService } from 'app/modules/social/services/presentation/presentation.service';
import { SceneService } from 'app/modules/social/services/scene.service';
import { ShareScreenService } from 'app/modules/social/services/share-screen/share-screen.service';
import {
  GroupShareScreenParticipantService,
  GroupShareState,
} from 'app/modules/social/services/share-screen-group/group-share-screen-participant.service';
import { GroupShareScreenService } from 'app/modules/social/services/share-screen-group/group-share-screen.service';
import { SocialMessageService } from 'app/modules/social/services/social-message.service';
import { PLAYERS_IN_VIEW_INTERVAL } from 'app/modules/social/services/subscription/subscription.types';
import { LoadingTransitionLabel, TransitionService } from 'app/modules/social/services/transition.service';
import { Player } from 'app/modules/social/social-pixi-canvas/player';
import { InteractionModePresets } from 'app/modules/social/social-state-machine/social-state.constants';
import {
  ExtendedPresenter,
  socialAppActions,
  socialAppSelectors,
  SpecialAccessGroupModalMode,
  SpecialAccessGroupModalState,
} from 'app/modules/social/state/app';
import { SocialMediaShareService } from 'app/shared/services/social-media-share.service';
import { SocialMediaShareModalComponent } from 'app/shared/social-media-share-modal/social-media-share-modal.component';
import { AppState } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import {
  ANYONE_CAN_PRESENT,
  ANYONE_CAN_SHARE,
  ANYONE_CAN_SHARE_TO_GROUP,
  PHOTO_BOOTH,
  SAG_ENABLED,
} from 'entity/SocialSettings';
import User from 'entity/User';

import { PresentationEmbedModalComponent } from '../../presentation-embed-modal/presentation-embed-modal.component';

import { TransitionPresentationService } from './../../../services/transition-presentation.service';
import { PresentationMenuType } from './presentation-menu-dropdown.types';
import { delayFor } from 'app/core/utilities/async-utils';
import { WebsocketService } from 'app/modules/social/services/websocket.service';
import { ConvertGroupMessage, OutgoingMessageType } from 'messages/websocket.messages';
import { BrowserService } from 'app/modules/social/services/browser.service';

interface PhotoBoothNotification {
  message: string;
  duration: number;
}

@Component({
  selector: 'app-presentation-menu-dropdown',
  templateUrl: './presentation-menu-dropdown.component.html',
  styleUrls: ['./presentation-menu-dropdown.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationMenuDropdownComponent extends Loggable implements OnInit, OnDestroy {
  //Event to tell the parent if the menu is up, in which case it can do some z-index change
  //to prevent 'Capture Debug Data' button to stand on top of the opened menu.
  //If the menu is closed the button's z-index must be higher than the menu's.
  @Output() public showDropdownEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public dropdownSelected = new EventEmitter<void>();
  @Input() public type: PresentationMenuType = PresentationMenuType.GROUP;
  @Input() public user?: User;
  @Input() public activeGroup?: boolean;

  public isPresentationActive = false;
  public isGroupSharing = false;
  public allowPhotoBooth = false;
  protected MediaEmbedType = MediaEmbedType;

  private _showDropdown = false;
  protected isHost = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private social?: Social;

  protected isLoading = false;
  protected loadingText = '';
  protected groupShareState = GroupShareState;
  protected groupShareButtonText = this.groupShareState.NOT_SHARING;

  private group: Player[] = [];
  protected isPlayerOutsideGroupLimit = false;
  protected isGroupShareEnabled = false;
  protected allowGroupShareScreen = false;
  protected userCanGroupScreenShare = false;
  protected allowAnyonePresent = false;
  protected allowAnyoneShareScreen = false;
  protected enhancedScreenShare = false;
  protected hasClosedMenu = false;
  protected isConnectedRoom = false;
  // TODO: set this to true when the feature is enabled
  protected isSomeoneElsePresenting = false;
  protected isPresenting = false;
  protected vimeoEmbed = false;
  protected activeMediaEmbed = false;
  protected get canScreenShare() {
    return (this.allowAnyoneShareScreen || this.isHost || this.isPresenting) && !this.activeMediaEmbed;
  }

  protected get canPresentToAllRooms() {
    return this.isConnectedRoom && (this.allowAnyonePresent || this.isHost);
  }

  protected isMobile = false;

  // SAG
  protected specialAccessGroups = false;
  protected allowSpecialAccessGroup = false;
  protected isGroupSAG = false;
  protected queueLength = 0;
  private sagModal: SpecialAccessGroupModalState = { mode: SpecialAccessGroupModalMode.REQUEST_ACCESS, open: false };

  public constructor(
    private dialog: MatDialog,
    public groupShareScreenService: GroupShareScreenService,
    private messageService: SocialMessageService,
    public photoBoothService: PhotoBoothService,
    private presentationService: PresentationService,
    private shareScreenService: ShareScreenService,
    private sceneService: SceneService,
    private socialMediaShareService: SocialMediaShareService,
    private store: Store<AppState>,
    private groupShareScreenParticipantService: GroupShareScreenParticipantService,
    private transitionService: TransitionService,
    private transitionPresentationService: TransitionPresentationService,
    private changeDetectorRef: ChangeDetectorRef,
    private websocketService: WebsocketService,
    private browserService: BrowserService,
  ) {
    super();
    this.setDisplayName('PresentationMenuDropdownComponent');

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresenting) => {
        this.isPresenting = isPresenting;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getPresenterDetails)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroyed$))
      .subscribe((presenters: ExtendedPresenter[]) => {
        const mediaUser = presenters.find((slot: ExtendedPresenter) => slot.media);
        this.activeMediaEmbed = !!mediaUser?.media;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_group_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isEnabled) => {
        this.isGroupShareEnabled = isEnabled;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.special_access_groups)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((specialAccessGroups) => {
        this.specialAccessGroups = specialAccessGroups;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => {
        this.isHost = isHost;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isPresentationActiveInAnotherRoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActiveInAnotherRoom) => {
        this.isSomeoneElsePresenting = isPresentationActiveInAnotherRoom;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        this.isPresentationActive = isPresentationActive;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.vimeo_embed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((vimeo_embed) => {
        this.vimeoEmbed = vimeo_embed;
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.store.select(socialAppSelectors.isHost),
      this.sceneService.isFeatureAllowed(PHOTO_BOOTH),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_PRESENT),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_SHARE),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_SHARE_TO_GROUP),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isHost, allowPhotoBooth, allowAnyonePresent, allowAnyoneShareScreen, allowGroupShareScreen]) => {
        this.isMobile = this.browserService.isMobile();

        this.isHost = isHost;
        this.allowPhotoBooth = !this.isMobile && allowPhotoBooth;
        this.allowAnyonePresent = allowAnyonePresent;
        this.allowAnyoneShareScreen = allowAnyoneShareScreen;
        this.allowGroupShareScreen = !this.isMobile && allowGroupShareScreen;
        this.changeDetectorRef.markForCheck();
        this.updateUserCanGroupScreenShare();
      });

    this.store
      .select(socialAppSelectors.getRoomSettings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const allowSpecialAccessGroup = this.sceneService.getSetting(SAG_ENABLED) ?? false;
        this.allowSpecialAccessGroup = !!allowSpecialAccessGroup;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(startWith({} as Social), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([room]) => {
        this.social = room;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.specialAccessGroupModal)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((sagModal) => {
        this.sagModal = sagModal;
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.sceneService.primaryGroupMembers$.pipe(
        throttle(() => interval(PLAYERS_IN_VIEW_INTERVAL), { leading: true }),
      ),
      this.groupShareScreenParticipantService.isGroupShareActive$,
      this.transitionService.isTransitioning$,
      this.transitionPresentationService.isTransitioning$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([group, isActive, isTransitioning, isPresentationTransitioning]) => {
        this.group = group;
        const mainPlayer = this.sceneService.getPlayer();
        this.isPlayerOutsideGroupLimit = !!mainPlayer?.groupWarning;
        this.updateUserCanGroupScreenShare();

        this.isGroupSharing = isActive;
        this.calculateGroupShareButtonText();

        if (!isActive || this.isPlayerOutsideGroupLimit) {
          this.hasClosedMenu = false;
        }

        const shouldFlyoutBeOpen = !!(
          isActive &&
          this.type === PresentationMenuType.GROUP &&
          mainPlayer &&
          this.group.includes(mainPlayer) &&
          !this.isPlayerOutsideGroupLimit &&
          !isTransitioning &&
          !isPresentationTransitioning &&
          !this.hasClosedMenu
        );
        this.showDropdown = shouldFlyoutBeOpen;
        // on share end reset share consent
        if (!isActive) {
          this.groupShareScreenParticipantService.userHasJoinedShare = false;
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.store.select(socialAppSelectors.isConnectedRoom),
      this.store.select(socialAppSelectors.getCurrentConnectedRoomSet),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isConnectedRoom, currentConnectedRoomCounts]) => {
        this.isConnectedRoom =
          isConnectedRoom && !!currentConnectedRoomCounts?.socials && currentConnectedRoomCounts?.socials?.length > 1;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enhanced_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enhanced_screen_share) => {
        this.enhancedScreenShare = enhanced_screen_share;
        this.changeDetectorRef.markForCheck();
      });

    this.sceneService.sceneManager?.sagMap$?.pipe(takeUntil(this.destroyed$)).subscribe((sagMap) => {
      const groupId = this.sceneService.sceneManager?.player?.groupId;
      if (!groupId) {
        this.error('GroupId is not defined');
        return;
      }
      const usersGroup = sagMap.get(groupId);
      this.queueLength = usersGroup?.queue.length ?? 0;
      this.isGroupSAG = !!usersGroup?.admitted.find((participant) => participant.userId === this.user?.userId);
      this.changeDetectorRef.markForCheck();
    });
  }

  private calculateGroupShareButtonText(): void {
    this.groupShareButtonText = this.groupShareScreenParticipantService.groupShareState;
  }

  public updateUserCanGroupScreenShare(): void {
    window.setTimeout(() => {
      this.userCanGroupScreenShare =
        this.isGroupShareEnabled &&
        (this.allowGroupShareScreen || this.isHost) &&
        this.group.length > 1 &&
        !this.isPlayerOutsideGroupLimit &&
        !this.groupShareScreenParticipantService.isGroupShareFull &&
        !this.groupShareScreenParticipantService.hasMembersInScreenShare;
      this.changeDetectorRef.markForCheck();
    }, 0);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    window.removeEventListener('click', this.windowClickListener);
    window.removeEventListener('touchstart', this.windowClickListener);
  }

  public showDropDownGroup(type: PresentationMenuType): boolean {
    switch (type) {
      case 'menuGroup':
        if (!this.isMobile) {
          return (
            this.allowPhotoBooth || this.userCanGroupScreenShare || this.isGroupSharing || this.allowSpecialAccessGroup
          );
        } else {
          return this.allowSpecialAccessGroup;
        }
      case 'menuPresentation':
        return this.allowAnyonePresent || this.allowAnyoneShareScreen;
      default:
        return false;
    }
  }

  public get showDropdown(): boolean {
    return this._showDropdown;
  }

  public set showDropdown(show: boolean) {
    if (show === this._showDropdown) {
      return;
    }
    this._showDropdown = show;
    this.showDropdownEvent.emit(show);
    // Set window listeners to catch clicks outside of the dropdown
    if (show) {
      this.addWindowListeners();
    } else {
      this.removeWindowListeners();
    }
  }

  public toggleDropdown($event: Event): void {
    $event.stopPropagation();
    this.showDropdown = !this.showDropdown;
    this.dropdownSelected.emit();
  }

  public dropdownClicked(e: Event): void {
    // Stop propagation so the dropdown doesn't close when clicked
    e.stopPropagation();
  }

  /**
   * Handle clicks on the window, to close the dropdown when the user clicks anywhere else on the window outside
   * of the dropdown
   **/
  private windowClickListener = (e: MouseEvent | TouchEvent) => {
    const { offsetParent } = e.target as HTMLElement;
    // Don't hide the dropdown if the toggle is being clicked
    if (offsetParent?.classList.contains(this.type) && offsetParent?.classList.contains('dropdown-button')) {
      return;
    }
    this.showDropdown = false;
    this.closedFlyoutInGroupShare();
  };

  private addWindowListeners(): void {
    window.addEventListener('click', this.windowClickListener);
    window.addEventListener('touchstart', this.windowClickListener);
  }

  private removeWindowListeners(): void {
    window.removeEventListener('click', this.windowClickListener);
    window.removeEventListener('touchstart', this.windowClickListener);
  }

  // Handle click on start presentation
  public async handleQuickPresent(crossroom = false, mediaEmbedType?: MediaEmbedType | undefined): Promise<void> {
    this.logUserAction('quick present', 'clicked');

    if (!this.user) {
      return;
    }

    const userCount = this.sceneService.getUserCount();
    const presentationSettings =
      userCount > 25 ? InteractionModePresets.ALL_HANDS : InteractionModePresets.OPEN_AUDIENCE_PARTICIPATION;

    await this.presentationService.startPresentation({
      selectedPresenters: [this.user],
      settings: presentationSettings,
      isCrossRoom: crossroom,
      mediaEmbedType,
    });
  }

  public handleEmbedPresentation(crossroom = false) {
    const dialogRef = this.dialog.open(PresentationEmbedModalComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: { mediaEmbedType: MediaEmbedType; embedCode: string } | null) => {
        if (!result) {
          return;
        }

        if (!this.user) {
          return;
        }

        const userCount = this.sceneService.getUserCount();

        const presentationSettings =
          userCount > 25 ? InteractionModePresets.ALL_HANDS : InteractionModePresets.OPEN_AUDIENCE_PARTICIPATION;

        if (this.isPresentationActive) {
          void this.presentationService.updatePresentationMediaState({
            url: result.embedCode,
            playing: false,
            startTime: 0,
            playtingTime: 0,
          });
          return;
        }

        void this.presentationService.startPresentation({
          selectedPresenters: [this.user],
          settings: presentationSettings,
          isCrossRoom: crossroom,
          mediaEmbedType: result.mediaEmbedType,
          embedCode: result.embedCode,
        });
      });
  }

  // Handle click on start screen share to room
  public async handleQuickScreenShare(enhanced = false): Promise<void> {
    this.logUserAction('quick screen share', 'clicked');

    if (!this.user) {
      return;
    }
    const shareScreenSuccess = await this.shareScreenService.startSharing(enhanced);

    if (shareScreenSuccess) {
      if (!this.isPresentationActive) {
        await this.presentationService.startPresentation({
          selectedPresenters: [this.user],
        });
      }
    }
  }

  // Handle click on start screen share to group
  public async handleQuickGroupScreenShare(enhanced = false): Promise<void> {
    if (!this.user || !this.user.userId) {
      return;
    }

    // if the user is already sharing to the group, stop sharing
    if (this.isGroupSharing && this.groupShareScreenService.isHost()) {
      await this.groupShareScreenService.stopSharing(this.user.userId);
      return;
    }

    // if the user is a participant in the group share, stop sharing for them
    if (this.isGroupSharing && this.groupShareScreenParticipantService.userHasJoinedShare) {
      // Starts loading effect
      this.transitionService
        .enableFadeInBackground()
        .then(() => {
          this.groupShareScreenParticipantService.userHasJoinedShare = false;
          this.calculateGroupShareButtonText();
        })
        .catch((err) => {
          this.error('Failed to execute transition', err);
        });

      return;
    }

    // if the user is a group member and the group share is active, join the group share
    if (this.isGroupSharing && !this.groupShareScreenParticipantService.userHasJoinedShare) {
      this.transitionService
        .enableDarkModeEffect(LoadingTransitionLabel.GROUPSHARE, true, true)
        .then(() => {
          this.groupShareScreenParticipantService.userHasJoinedShare = true;
          this.calculateGroupShareButtonText();
        })
        .catch((err) => {
          this.error('Failed to execute transition', err);
        });

      return;
    }

    this.logUserAction('quick group screen share', 'clicked');

    await this.groupShareScreenService.startSharing(enhanced);
  }

  // Handle click on take a group photo
  public showPhotoBoothModal($event: Event): void {
    $event.stopPropagation();
    $event.preventDefault();
    const dialog = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data: {
        modalTitle: 'Photo booth is ready to start',
        text: 'The photo booth will take 3 consecutive photos of your room, which you can save and share with your friends and colleagues!',
        primaryButtonText: 'Snap Photos',
        primaryButtonIcon: 'camera_alt',
      },
    });
    dialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'primary') {
          this.photoBoothService.reset();
          this.photoBoothApproved();
        }
      });
  }

  private photoBoothApproved(): void {
    this.photoBoothService.boothStartedByUser = true;
    this.photoBoothService.boothInSession = false;
    this.startPhotoTakingProcess([
      { message: '{{name}} turned on the photo booth! Smile for the first picture in', duration: 5 },
      { message: 'Great! Snapping the next picture in', duration: 3 },
      { message: 'Last one! Smile in', duration: 3 },
    ]).catch((error) => {
      this.error('Failed to start photo booth', error);
    });
  }

  private async startPhotoTakingProcess(notifications: PhotoBoothNotification[]): Promise<void> {
    for (const notification of notifications) {
      this.sendMessageToPlayersInView(notification);
      const canceled = await this.photoBoothService.startCountdown(
        notification.message.replace('{{name}}', 'You'),
        notification.duration,
      );
      if (canceled) {
        return;
      }
      await this.photoBoothService.takePhoto(this.social?.theme?.colorGradientBackground);
    }
    this.isLoading = true;
    this.loadingText =
      'We’re loading the preview of your pictures. This may take a minute. Don’t refresh or click the back button.';
    const gif = await this.photoBoothService.createGif(this.photoBoothService.photos);
    this.photoBoothService.photos.push(gif);
    const viewerData = await this.generateViewerData();
    this.isLoading = false;
    this.openViewer(viewerData);
  }

  private sendMessageToPlayersInView(notification: PhotoBoothNotification): void {
    assertDefined(this.user);
    const message: Message = {
      type: MessageType.PHOTO_BOOTH_START,
      message: notification.message.replace('{{name}}', `${formatUserName(this.user)}`),
      photoBoothData: { notificationDuration: notification.duration },
      onlyPlayers: this.sceneService.getPlayersIds(),
    };
    this.messageService.sendMessage(message);
  }

  private async generateViewerData(): Promise<PhotoViewerData> {
    const photoBoothPictures: PhotoBoothPicture[] = [];
    this.photoBoothService.photos.forEach((url, id) => photoBoothPictures.push({ url, id }));
    const framedPictures = await this.photoBoothService.addPreciateFrame(
      photoBoothPictures,
      moment().format('MM.DD.YYYY'),
      this.social?.name ?? '',
    );
    return { photoBoothPictures, framedPictures };
  }

  private openViewer(data: PhotoViewerData): void {
    this.photoBoothService.boothStartedByUser = false;
    const dialog = this.dialog.open(PhotoBoothViewerComponent, {
      panelClass: 'photo-booth-viewer',
      data,
    });
    dialog.componentInstance.socialName = (this.social?.name ?? '').replace(/[^\w\s]/gi, '');
    dialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (idSelectedToShare) => {
        if (!isNaN(idSelectedToShare)) {
          const selectedFramedPicture = data.framedPictures.find((fp) => fp.id === idSelectedToShare);
          if (!selectedFramedPicture) {
            return;
          }
          const url = await this.uploadPicture(selectedFramedPicture);
          this.dialog.open(SocialMediaShareModalComponent, {
            panelClass: 'photo-booth-viewer',
            data: {
              url,
              description: `This pic was snapped on #scootsocial - Create your own room at https://scoot.app`,
            },
          });
        }
      });
  }

  public async uploadPicture(imageToShare: FramedPicture): Promise<string> {
    this.isLoading = true;
    this.loadingText = 'Preparing your picture...';
    const { base64, height, width } = imageToShare;
    const type = imageToShare.type.split('/')[1];
    try {
      const uploadedImage = await this.socialMediaShareService.uploadImage(type, width, height, base64);
      const imageURI = `${uploadedImage.imageId}.${type}`;
      const baseUrl = 'https://scoot.app';
      return `${baseUrl}/imgs/public/${imageURI}`;
    } catch (error) {
      this.error('Failed to upload picture', error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  private closedFlyoutInGroupShare() {
    if (this.type === 'menuGroup' && this.groupShareScreenParticipantService.userHasJoinedShare) {
      this.hasClosedMenu = true;
    }
  }

  // SAG
  protected enableSpecialAccessGroup() {
    const msg: ConvertGroupMessage = {
      t: OutgoingMessageType.CONVERT_GROUP,
      sag: true,
    };
    this.websocketService.sendMessage(msg);
    this.changeDetectorRef.markForCheck();
  }

  protected disableSpecialAccessGroup() {
    const msg: ConvertGroupMessage = {
      t: OutgoingMessageType.CONVERT_GROUP,
      sag: false,
    };

    this.websocketService.sendMessage(msg);
    this.changeDetectorRef.markForCheck();
  }

  protected async openRequestQueue() {
    this.showDropdown = !this.showDropdown;
    this.dropdownSelected.emit();
    // await dropdown close animation for UX propouse
    await delayFor(200);
    this.store.dispatch(
      socialAppActions.UPDATE_SAG_MODAL({ mode: SpecialAccessGroupModalMode.ALLOW_ACCESS, open: true }),
    );
    this.changeDetectorRef.markForCheck();
  }

  protected get showNotification() {
    return (
      !!this.queueLength &&
      this.type === 'menuGroup' &&
      this.specialAccessGroups &&
      this.allowSpecialAccessGroup &&
      this.isGroupSAG
    );
  }

  protected get notificationText() {
    return this.queueLength > 9 ? '9+' : `${this.queueLength}`;
  }

  protected get showEnableSagButton() {
    return this.allowSpecialAccessGroup && this.specialAccessGroups && !this.isGroupSAG && !this.isSagModalOpen;
  }

  protected get showInGroupButton() {
    return this.allowSpecialAccessGroup && this.specialAccessGroups && this.isGroupSAG;
  }

  private get isSagModalOpen() {
    return this.sagModal.open && this.sagModal.mode === SpecialAccessGroupModalMode.REQUEST_ACCESS;
  }
}
